import { Controller } from 'stimulus'

const POLL_INTERVAL_MS = 5000

export default class extends Controller {
  static targets = [
    'button',
    'confirmationButton',
    'confirmationMessage',
    'confirmationModal',
    'onePiecePerTruck',
    'status',
    'supportLength',
    'supportLengthApplyButton',
    'supportLengthBulkInput',
    'supportLengthSelectAll',
    'supportLengthSelection',
  ]

  static values = {
    activeRunId: String,
    confirmMessage: String,
    projectUrl: String,
    refreshOnTerminal: Boolean,
    rerun: Boolean,
    runningMessage: String,
    statusUrlTemplate: String,
  }

  connect() {
    this.activeRunId = this.hasActiveRunIdValue ? this.activeRunIdValue : null
    this.currentRunRerun = this.hasRerunValue ? this.rerunValue : false
    this.startedByThisPage = false
    this.terminalHandled = false
    this.subscription = null
    this.pollTimer = null
    this.pendingForm = null
    this.confirmationTrigger = null
    this.confirmationModalBinding = null
    this.confirmationHiddenHandler = null
    this.bindConfirmationLifecycle()
    this.updateSupportLengthBulkState()

    if (this.activeRunId) {
      this.setRunningState(this.runningMessage())
      this.subscribeToRun(this.activeRunId)
      this.startStatusPolling()
    }
  }

  disconnect() {
    this.unbindConfirmationLifecycle()
    this.stopStatusPolling()
    this.unsubscribeFromRun()
  }

  bindConfirmationLifecycle() {
    if (!this.hasConfirmationModalTarget) return

    const modal = this.bootstrapModal(this.confirmationModalTarget)
    if (!modal || typeof modal.on !== 'function') return

    this.confirmationModalBinding = modal
    this.confirmationHiddenHandler = () => this.handleConfirmationHidden()
    modal.on('hidden.bs.modal.projectLoadPlanningRun', this.confirmationHiddenHandler)
  }

  unbindConfirmationLifecycle() {
    if (this.confirmationModalBinding && this.confirmationHiddenHandler && typeof this.confirmationModalBinding.off === 'function') {
      this.confirmationModalBinding.off('hidden.bs.modal.projectLoadPlanningRun', this.confirmationHiddenHandler)
    }

    this.confirmationModalBinding = null
    this.confirmationHiddenHandler = null
  }

  handleConfirmationHidden() {
    if (!this.hasConfirmationModalTarget) return

    const modal = this.confirmationModalTarget
    modal.classList.remove('in')
    modal.style.display = 'none'
    modal.setAttribute('aria-hidden', 'true')
  }

  async submit(event) {
    event.preventDefault()

    if (this.activeRunId) return

    const form = event.currentTarget
    if (this.hasConfirmMessageValue && this.confirmMessageValue) {
      this.pendingForm = form
      this.confirmationTrigger = event.submitter || form.querySelector('[type="submit"]')
      this.openConfirmation()
      return
    }

    await this.startRun(form)
  }

  async confirm(event) {
    event.preventDefault()

    const form = this.pendingForm
    if (!form || this.activeRunId) return

    this.pendingForm = null
    this.closeConfirmation({ restoreFocus: false })
    await this.startRun(form)
  }

  cancelConfirmation(event) {
    if (event) event.preventDefault()

    this.pendingForm = null
    this.closeConfirmation({ restoreFocus: true })
  }

  async startRun(form) {
    const formData = new FormData(form)
    this.appendOnePiecePerTruck(formData)
    this.appendSupportLengths(formData)
    this.startedByThisPage = true
    this.terminalHandled = false
    this.currentRunRerun = this.formDataBoolean(formData, 'rerun')
    this.setRunningState('Starting load planning...')

    try {
      const response = await fetch(form.action, {
        method: form.method || 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
      })
      const payload = await this.parseJson(response)

      if (!response.ok) {
        this.clearRunningState()
        this.showStatus(this.errorMessageFor(payload), 'danger')
        return
      }

      this.handleRunUpdate(payload)
      if (!this.runTerminal(payload)) {
        this.subscribeToRun(payload.run_id)
        this.startStatusPolling()
      }
    } catch (error) {
      this.clearRunningState()
      const message = error instanceof Error ? error.message : 'Could not start load planning.'
      this.showStatus(message, 'danger')
    }
  }

  openConfirmation() {
    if (!this.hasConfirmationModalTarget) return

    if (this.hasConfirmationMessageTarget) {
      this.confirmationMessageTarget.textContent = this.confirmMessageValue
    }

    const modal = this.confirmationModalTarget
    const bootstrap = this.bootstrapModal(modal)
    if (bootstrap) {
      bootstrap.modal('show')
    } else {
      modal.classList.add('in')
      modal.style.display = 'block'
    }
    modal.setAttribute('aria-hidden', 'false')

    window.setTimeout(() => {
      if (this.hasConfirmationButtonTarget) this.confirmationButtonTarget.focus()
    }, 0)
  }

  closeConfirmation({ restoreFocus }) {
    if (this.hasConfirmationModalTarget) {
      const modal = this.confirmationModalTarget
      const bootstrap = this.bootstrapModal(modal)
      if (bootstrap) {
        bootstrap.modal('hide')
      } else {
        modal.classList.remove('in')
        modal.style.display = 'none'
      }
      modal.setAttribute('aria-hidden', 'true')
    }

    if (restoreFocus && this.confirmationTrigger) {
      window.setTimeout(() => this.confirmationTrigger && this.confirmationTrigger.focus(), 0)
    }
    this.confirmationTrigger = null
  }

  bootstrapModal(element) {
    const jquery = window.jQuery || window.$
    if (typeof jquery !== 'function') return null

    const modal = jquery(element)
    return modal && typeof modal.modal === 'function' ? modal : null
  }

  handleRunUpdate(payload) {
    if (!payload || !payload.run_id) return
    if (this.activeRunId && payload.run_id.toString() !== this.activeRunId.toString()) return

    this.activeRunId = payload.run_id.toString()
    if (payload.rerun !== undefined) this.currentRunRerun = payload.rerun === true

    if (this.runTerminal(payload)) {
      this.handleTerminalRun(payload)
      return
    }

    this.setRunningState(payload.message || this.runningMessage())
  }

  handleTerminalRun(payload) {
    if (this.terminalHandled) return

    this.terminalHandled = true
    this.stopStatusPolling()
    this.unsubscribeFromRun()
    this.activeRunId = null

    const message = payload.message || this.errorMessageFor(payload)
    const type = this.alertTypeFor(payload.status)
    this.showStatus(message, type)

    if (this.shouldRedirectAfterTerminal(payload)) {
      window.setTimeout(() => {
        window.location = payload.project_url || this.projectUrl()
      }, 900)
    } else {
      this.clearRunningState()
    }
  }

  subscribeToRun(runId) {
    if (!runId || this.subscription) return

    const consumer = this.cableConsumer()
    if (!consumer) return

    this.subscription = consumer.subscriptions.create(
      {
        channel: 'ProjectLoadPlanningRunChannel',
        run_id: runId,
      },
      {
        connected: () => {
          this.fetchRunStatus()
          this.startStatusPolling()
        },
        disconnected: () => this.startStatusPolling(),
        rejected: () => this.startStatusPolling(),
        received: (payload) => this.handleRunUpdate(payload),
      }
    )
  }

  unsubscribeFromRun() {
    const consumer = this.cableConsumer()
    if (consumer && this.subscription) {
      consumer.subscriptions.remove(this.subscription)
    }
    this.subscription = null
  }

  startStatusPolling() {
    if (!this.activeRunId || this.pollTimer) return

    this.pollTimer = window.setInterval(() => this.fetchRunStatus(), POLL_INTERVAL_MS)
    this.fetchRunStatus()
  }

  stopStatusPolling() {
    if (!this.pollTimer) return

    window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async fetchRunStatus() {
    if (!this.activeRunId) return

    try {
      const response = await fetch(this.statusUrl(this.activeRunId), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return

      this.handleRunUpdate(await response.json())
    } catch (_error) {
      // Polling is a fallback for cable disconnects; keep the current UI state.
    }
  }

  statusUrl(runId) {
    const template = this.hasStatusUrlTemplateValue ? this.statusUrlTemplateValue : ''
    return template.replace('__RUN_ID__', encodeURIComponent(runId))
  }

  runTerminal(payload) {
    if (payload.terminal === true) return true

    return ['succeeded', 'partial_success', 'no_eligible_units', 'failed'].includes((payload.status || '').toString())
  }

  setRunningState(message) {
    this.buttonTargets.forEach((button) => {
      button.disabled = true
      if (button.tagName.toLowerCase() === 'button') {
        if (!button.dataset.projectLoadPlanningIdleLabel) {
          button.dataset.projectLoadPlanningIdleLabel = button.textContent
        }
      }
    })
    this.showStatus(message, 'info')
  }

  clearRunningState() {
    this.buttonTargets.forEach((button) => {
      button.disabled = false
      if (button.tagName.toLowerCase() === 'button' && button.dataset.projectLoadPlanningIdleLabel) {
        button.textContent = button.dataset.projectLoadPlanningIdleLabel
      }
    })
  }

  showStatus(message, type = 'info') {
    const pageStatus = this.pageStatusElement()
    if (pageStatus) {
      const messageElement = pageStatus.querySelector('[data-project-load-planning-run-page-status-message]') || pageStatus
      messageElement.textContent = message
      pageStatus.classList.remove('hidden', 'alert-danger', 'alert-warning', 'alert-success', 'alert-info')
      pageStatus.classList.add(`alert-${type}`)

      if (this.hasStatusTarget) {
        this.statusTarget.textContent = ''
        this.statusTarget.classList.add('hidden')
      }
      return
    }

    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove('hidden', 'text-danger', 'text-warning', 'text-success', 'text-info')
    this.statusTarget.classList.add(`text-${type}`)
  }

  pageStatusElement() {
    if (!this.element || typeof this.element.closest !== 'function') return null

    const workspace = this.element.closest('#project-load-management-workspace')
    if (!workspace) return null

    return workspace.querySelector('[data-project-load-planning-run-page-status]')
  }

  alertTypeFor(status) {
    if (status === 'failed') return 'danger'
    if (status === 'partial_success') return 'warning'
    if (status === 'no_eligible_units') return 'info'

    return 'success'
  }

  errorMessageFor(payload) {
    const readinessMessage = this.readinessMessageFor(payload)
    if (readinessMessage) return readinessMessage
    if (payload && payload.error) return payload.error
    if (payload && payload.error_message) return payload.error_message

    return 'Load planning failed. Please try again.'
  }

  readinessMessageFor(payload) {
    const issues = payload && payload.readiness && Array.isArray(payload.readiness.issues) ? payload.readiness.issues : []
    if (!issues.length) return null

    const issueText = issues
      .map((issue) => {
        const loads = Number(issue.loads || 0)
        const rows = Number(issue.rows || 0)
        return `${loads} ${loads === 1 ? 'piece' : 'pieces'} across ${rows} ${rows === 1 ? 'row' : 'rows'} ${issue.message}.`
      })
      .join(' ')
    return `${payload.error || 'Some pieces need updates before load planning can run.'} ${issueText}`
  }

  shouldRedirectAfterTerminal(payload) {
    const refreshMatrixSummary = this.hasRefreshOnTerminalValue && this.refreshOnTerminalValue
    if (!this.startedByThisPage && !refreshMatrixSummary) return false

    const status = (payload.status || '').toString()
    if (status === 'succeeded') return true
    // A matrix rerun can usefully mutate the saved plan even when it creates
    // no assignments or a later batch fails: completed/rejected scenarios can
    // replace prior drafts and reconciliation can prune stale drafts.
    if (refreshMatrixSummary && ['partial_success', 'no_eligible_units', 'failed'].includes(status)) return true
    if (status !== 'partial_success') return false

    const result = payload.result && typeof payload.result === 'object' ? payload.result : payload
    return Number(result.planned_assignments_count || 0) > 0
  }

  formDataBoolean(formData, key) {
    const value = formData.get(key)
    return value === true || value === 'true' || value === '1' || value === 'on'
  }

  appendSupportLengths(formData) {
    if (!this.hasSupportLengthTarget) return

    this.supportLengthTargets.forEach((input) => {
      const value = (input.value || '').trim()
      if (value) formData.append(input.name, value)
    })
  }

  appendOnePiecePerTruck(formData) {
    if (!this.hasOnePiecePerTruckTarget) return

    formData.set('one_piece_per_truck', this.onePiecePerTruckTarget.checked ? '1' : '0')
  }

  toggleAllSupportLengthRows(event) {
    const checked = event.currentTarget.checked
    this.supportLengthSelectionTargets.forEach((checkbox) => { checkbox.checked = checked })
    this.updateSupportLengthBulkState()
  }

  updateSupportLengthBulkState() {
    const checkboxes = this.hasSupportLengthSelectionTarget ? this.supportLengthSelectionTargets : []
    const selectedCount = checkboxes.filter((checkbox) => checkbox.checked).length

    if (this.hasSupportLengthSelectAllTarget) {
      this.supportLengthSelectAllTargets.forEach((checkbox) => {
        checkbox.checked = checkboxes.length > 0 && selectedCount === checkboxes.length
        checkbox.indeterminate = selectedCount > 0 && selectedCount < checkboxes.length
      })
    }

    if (this.hasSupportLengthApplyButtonTarget) {
      const value = this.hasSupportLengthBulkInputTarget ? this.supportLengthBulkInputTarget.value.trim() : ''
      this.supportLengthApplyButtonTarget.disabled = selectedCount === 0 || value.length === 0
    }
  }

  applySupportLengthToSelected(event) {
    event.preventDefault()
    if (!this.hasSupportLengthBulkInputTarget) return

    const input = this.supportLengthBulkInputTarget
    this.normalizeSupportLengthInput(input)
    const value = input.value.trim()
    if (!value) return

    this.supportLengthSelectionTargets.forEach((checkbox) => {
      if (!checkbox.checked) return

      const rowInput = checkbox.closest('tr').querySelector('[data-project-load-planning-run-target~="supportLength"]')
      if (rowInput) rowInput.value = value
    })
  }

  normalizeSupportLength(event) {
    this.normalizeSupportLengthInput(event.currentTarget)
  }

  normalizeSupportLengthInput(input) {
    if (typeof window.normalizeDimensionInputElement !== 'function') return

    window.normalizeDimensionInputElement(input, 'rfq_commodity_support_length')
  }

  runningMessage() {
    if (this.hasRunningMessageValue && this.runningMessageValue) return this.runningMessageValue

    return 'Load planning is running...'
  }

  projectUrl() {
    if (this.hasProjectUrlValue && this.projectUrlValue) return this.projectUrlValue

    return window.location.href
  }

  csrfToken() {
    const tag = document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.getAttribute('content') : ''
  }

  cableConsumer() {
    if (typeof window.getBulkImportCableConsumer === 'function') return window.getBulkImportCableConsumer()
    if (window.App && typeof window.App.getBulkImportCableConsumer === 'function') return window.App.getBulkImportCableConsumer()

    return null
  }

  async parseJson(response) {
    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }
}
